/** Storyteller — rolls timed incidents and recruit offers (deterministic).
 *  City-wide modifiers; local hits land on the active building. */
import type { City, RecruitOffer } from './types';
import { SIM, INCIDENTS, incidentById } from './config';
import { Rng } from './rng';
import { makeColonist, isAvailable } from './colonist';
import { pushLog, activeBuilding } from './world';

export function rollEvent(city: City, dt: number): void {
  city.activeEvents = city.activeEvents.filter((e) => e.endsAt > city.t);
  if (city.recruit && city.recruit.expiresAt <= city.t) city.recruit = null;

  city.eventTimer -= dt;
  if (city.eventTimer > 0) return;
  city.eventTimer = SIM.EVENT_INTERVAL_SEC;
  if (city.buildings.length === 0) return; // no operation yet — no storyteller

  const rng = new Rng(city.rngSeed);

  if (!city.recruit && rng.next() < 0.34) {
    city.recruit = makeRecruit(city, rng);
    city.rngSeed = rng.seed;
    pushLog(city, 'good', `🧍 ${city.recruit.colonist.name} is looking for work.`);
    return;
  }

  const idx = rng.weighted(INCIDENTS.map((e) => e.weight));
  city.rngSeed = rng.seed;
  if (idx < 0) return;
  applyIncident(city, INCIDENTS[idx].id, rng);
}

function makeRecruit(city: City, rng: Rng): RecruitOffer {
  const colonist = makeColonist(rng, activeBuilding(city)!.nextColonistId);
  return { colonist, cost: 140 + rng.int(160), expiresAt: city.t + SIM.RECRUIT_OFFER_SEC };
}

function applyIncident(city: City, id: string, rng: Rng): void {
  const def = incidentById(id);
  if (!def) return;
  const b = activeBuilding(city)!;

  switch (id) {
    case 'customer-rush':
    case 'fresh-batch':
    case 'morale-slump':
      city.activeEvents.push({ id, label: def.label, endsAt: city.t + (def.durationSec ?? 20) });
      pushLog(city, def.kind, `${def.kind === 'good' ? '✨' : '⚠️'} ${def.label}: ${def.blurb}`);
      break;
    case 'rival-shakedown': {
      b.res.product *= 0.8;
      city.cash = Math.max(0, city.cash - city.cash * 0.12);
      pushLog(city, 'bad', `🔫 ${def.label}: ${def.blurb}`);
      break;
    }
    case 'inspection':
      b.heat = Math.min(100, b.heat + 16);
      pushLog(city, 'bad', `👮 ${def.label}: ${def.blurb}`);
      break;
    case 'bad-trip': {
      const present = b.colonists.filter((c) => isAvailable(c, city.t));
      if (present.length) {
        const v = present[rng.int(present.length)];
        v.downUntil = city.t + SIM.INJURE_RECOVER_SEC;
        city.rngSeed = rng.seed;
        pushLog(city, 'bad', `💊 ${def.label}: ${v.name} is down for a while.`);
      }
      break;
    }
  }
}
